import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageTitle from '../components/shared/PageTitle';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/vendors/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error('Request failed');
      }
      setIsSubmitted(true);
    } catch {
      setError(t('auth.forgotPassword.failedToSend'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
        <PageTitle title={t('auth.forgotPassword.pageTitle')} />
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('auth.forgotPassword.emailSent')}</h1>
            <p className="text-gray-600 mb-6">
              {t('auth.forgotPassword.emailSentMessage', { email })
                .split(/<1>|<\/1>/)
                .map((chunk, i) => (i === 1 ? <strong key={i}>{chunk}</strong> : chunk))}
            </p>
            <p className="text-sm text-gray-500 mb-8">
              {t('auth.forgotPassword.checkInbox')}
            </p>

            <div className="space-y-4">
              <Link
                to="/login"
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>{t('auth.forgotPassword.backToLogin')}</span>
              </Link>

              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail('');
                }}
                className="w-full text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                {t('auth.forgotPassword.resendEmail')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
      <PageTitle title={t('auth.forgotPassword.pageTitle')} />
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/logo-color-favicone.png"
              alt="Clareefai"
              className="h-16 w-16"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{t('auth.forgotPassword.heading')}</h1>
          <p className="text-gray-600 mt-2">
            {t('auth.forgotPassword.description')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.emailAddress')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder={t('auth.forgotPassword.emailPlaceholder')}
                required
              />
            </div>
          </div>

          {error && (
            <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t('common.sending')}</span>
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" />
                <span>{t('auth.forgotPassword.sendLink')}</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('auth.forgotPassword.backToLogin')}</span>
          </Link>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-3">
            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">{t('auth.forgotPassword.needHelp')}</h4>
              <p className="text-sm text-blue-700">
                {t('auth.forgotPassword.needHelpText')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;